import Button from "@/comps/button"
import {
	getCurrentPlanText,
	TOKENS_TO_VIDEOS_DIVISOR,
} from "@/comps/subscription-popup"
import { SquareSwitch } from "@/comps/toggle"
import {
	OfferingInfoResponse,
	OfferingPrices,
} from "@/models/general"
import { useAdjustLogger } from "@/utils/adjust"
import useAuth from "@/utils/client-auth"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import { ReactNode } from "react"

interface CompareProps {
	sections: OfferingInfoResponse["result"]["package_compare"]["sections"]
	packages: OfferingInfoResponse["result"]["package_compare"]["packages"]
	prices: OfferingPrices
	isYearly: boolean
	setIsMonthly: (v: boolean) => void
	handlePurchaseClick: () => void
	packId: string
}

export default function Compare(props: CompareProps) {
	const {
		sections,
		isYearly,
		setIsMonthly,
		prices,
		handlePurchaseClick,
		packId,
	} = props
	const { plans, save_percent: savePercent } = prices
	const { t } = useTranslation()

	const { userInfo } = useAuth()
	const logAdjust = useAdjustLogger()
	const router = useRouter()

	const value =
		plans.advanced.variants?.findIndex(
			(a) => a.pack_id === packId,
		) ?? -1

	const yearlyPrice =
		plans.advanced.variants?.find(
			(a) => a.pack_id === packId,
		)?.yearly_price || 0
	const yearlyPriceDivided = yearlyPrice / 12
	const yearlyPriceFormatted = Math.floor(
		yearlyPriceDivided,
	)

	const currentPlanText = getCurrentPlanText(
		userInfo ? userInfo.entitlements : [],
		packId,
		isYearly,
		prices,
	)

	return (
		<div
			className={clsx(
				"flex flex-col items-center justify-center gap-[22px] tablet:mt-[40px]",
			)}>
			<span
				className={clsx(
					"text-[20px] font-700 leading-[24px] tracking-[0.4px] text-blue-900",
					"tablet:text-[24px] tablet:leading-[36px] tablet:tracking-[0.48px]",
					"pt-[50px] desktop:text-[32px] desktop:tracking-[0.64px]",
				)}>
				{t("txt_compare")}
			</span>
			<div className="flex w-full flex-row gap-2 px-4 tablet:hidden">
				<div className=" flex w-full items-center justify-center gap-[10px] tablet:hidden">
					<span
						className={clsx(
							isYearly ? "text-blue-500" : "text-blue-900",
							"text-[18px] font-500 transition-colors",
						)}>
						{t("txt_monthly")}
					</span>
					<SquareSwitch
						onChange={setIsMonthly}
						enabled={isYearly}
					/>
					<span
						className={clsx(
							isYearly ? "text-blue-900" : "text-blue-500",
							"text-[18px] font-500 transition-colors ",
						)}>
						{t("txt_yearly")}
					</span>
					<span
						className={clsx(
							"text-[18px] font-500 transition-colors",
							isYearly ? "text-blue-900" : "text-blue-500",
						)}>
						{t("fs_save_percent", {
							percent: String(savePercent),
						})}
					</span>
				</div>
			</div>
			<div
				className={clsx(
					"flex w-full flex-col rounded-[12px] desktop:w-[975px]",
					"px-4 tablet:px-5 desktop:px-0",
				)}>
				<div
					className={clsx(
						"z-20 w-full pt-[20px]",
						"sticky bottom-[100px] top-[29px] bg-color-background tablet:top-[50px]",
					)}>
					<div className="flex w-full flex-row items-center justify-between rounded-t-[12px] border-b border-color-separator bg-color-cell">
						<div className="ml-[50px] hidden w-[130px] flex-col tablet:flex">
							<div className="flex flex-row items-center justify-between">
								<span
									className={clsx(
										"text-[18px] font-500 text-blue-900",
									)}>
									{isYearly
										? t("txt_yearly")
										: t("txt_monthly")}
								</span>
								<SquareSwitch
									onChange={setIsMonthly}
									enabled={isYearly}
								/>
							</div>
							<span
								className={clsx(
									isYearly
										? "text-blue-800"
										: "text-blue-500",
									"text-[18px] font-500 transition-colors duration-200",
								)}>
								{t("fs_save_percent", {
									percent: String(savePercent),
								})}
							</span>
						</div>

						<div className="flex w-full flex-row justify-between tablet:w-auto tablet:justify-center">
							{/* Free Plan */}
							{/* <div className="flex flex-1 flex-col items-center gap-2 px-2 py-3 tablet:w-[170px] tablet:px-5 tablet:py-7 desktop:w-[209px]">
                <p className="text-[18px] font-500 text-blue-900">
                  {t("txt_just_try")}
                </p>
                <p className="text-[14px] font-500 uppercase text-blue-800">
                  {t("txt_free")}
                </p>
                <Button
                  href="/chat"
                  small
                  className="flex h-10 w-full items-center justify-center !p-0 !text-[12px] tablet:!text-[15px] desktop:w-[170px]"
                  outlineSecondary
                  text={"txt_get_started"}
                />
              </div> */}

							{/* Pro */}
							<div className="flex flex-1 flex-col items-center gap-2 rounded-t-[12px] border-2 border-b-0 border-green-400 px-2 py-3 tablet:w-[170px] tablet:px-5 tablet:py-7 desktop:w-[209px]">
								<p className="text-[18px] font-500 text-blue-900">
									{t("txt_pro_title")}
								</p>
								<div className="relative h-[20px] w-full overflow-hidden">
									<div
										className={clsx(
											"absolute inset-0",
											"flex h-[20px] w-full flex-row items-end justify-center gap-[2px]",
											isYearly && "-top-6",
											"transition-all duration-500",
										)}>
										<span
											className={clsx(
												"font-700 text-blue-800",
												"text-[14px]",
											)}>
											$
											{String(
												plans.advanced.variants?.find(
													(a) => a.pack_id === packId,
												)?.monthly_price,
											).slice(0, -2)}
											.
											<small>
												{String(
													plans.advanced.variants?.find(
														(a) => a.pack_id === packId,
													)?.monthly_price,
												).slice(-2)}
											</small>
										</span>
										<span className="font-300 mb-[2px] text-[10px] lowercase text-blue-500">
											/{t("txt_month")}
										</span>
									</div>

									<div
										className={clsx(
											"absolute inset-0",
											"flex h-[20px] w-full flex-row items-end justify-center gap-[2px]",
											!isYearly && "top-6",
											"transition-all duration-500",
										)}>
										<span
											className={clsx(
												"font-700 text-blue-900",
												"text-[14px]",
											)}>
											$
											{String(yearlyPriceFormatted).slice(
												0,
												-2,
											).length !== 0
												? String(
														yearlyPriceFormatted,
												  ).slice(0, -2)
												: 0}
											.
											<small>
												{String(yearlyPriceFormatted).slice(
													-2,
												)}
											</small>
										</span>
										<span className="font-300 mb-[2px] text-[10px] lowercase text-blue-600">
											/{t("txt_month")}*
										</span>
									</div>
								</div>
								<Button
									small
									secondary
									disabled={
										currentPlanText === "txt_current_plan"
									}
									onClick={handlePurchaseClick}
									className="flex h-10 w-full items-center justify-center !text-[12px] disabled:!bg-green-100 disabled:!text-color-inactive-text disabled:hover:!bg-green-100 tablet:!text-[15px] desktop:w-[170px]"
									text={currentPlanText}
								/>
							</div>

							{/* Enterprise */}
							<div className="flex flex-1 flex-col items-center gap-2 px-2 py-3 tablet:w-[170px] tablet:px-5 tablet:py-7 desktop:w-[209px]">
								<p className="text-[18px] font-500 text-blue-900">
									{t("txt_enterprise_title")}
								</p>
								<p className="text-[14px] font-500 text-blue-800">
									{t("txt_custom")}
								</p>
								<Button
									href={plans.enterprise.contact_url}
									onClick={() =>
										logAdjust?.logEventWithPayload(
											"w_dp_cs",
											{ from: router.asPath },
										)
									}
									small
									className="flex h-10 w-full items-center justify-center !p-0 !text-[12px] tablet:!text-[15px] desktop:w-[170px]"
									outlineSecondary
									text={"txt_contact_sales"}
								/>
							</div>
						</div>
					</div>
				</div>
				{sections.map((section, index) => {
					return (
						<div
							className={clsx(
								sections.length - 1 === index &&
									" rounded-b-[12px]",
								"flex w-full flex-col gap-[12px] bg-color-cell py-[14px]",
								"relative z-10 border-b border-color-separator tablet:pl-4 desktop:pl-[20px]",
							)}
							key={index}>
							<div className=" absolute -top-1 left-0 z-20 flex h-full w-full">
								<div className="hidden flex-1 tablet:block"></div>
								{/* Free Plan */}
								{/* <div className="w-full tablet:w-[170px] desktop:w-[209px]"></div> */}
								<div
									className={clsx(
										sections.length - 1 === index
											? "rounded-b-[12px] border-2 border-t-0"
											: "border-x-2",
										"relative h-[calc(100%+5px)] w-full border-green-400 tablet:w-[172px] desktop:w-[209px]",
									)}></div>
								<div className="w-full tablet:w-[169px] desktop:w-[209px]"></div>
							</div>

							<span
								className={clsx(
									"pl-3 text-[14px] font-700 uppercase tracking-[0.28px] tablet:pl-0",
									"text-blue-800",
								)}>
								{section.title}
							</span>

							{section.title === "Features" && (
								<div
									className={clsx(
										index !== 0 &&
											"border-t border-color-separator py-1 tablet:border-none",
										"flex w-full flex-col rounded-b-[12px] bg-color-cell",
										"tablet:flex-row tablet:py-3 desktop:py-4",
									)}
									key={index}>
									<div className="flex w-[100px] tablet:w-auto tablet:flex-1">
										<span
											className={clsx(
												"pl-3 text-start text-[14px] font-400 text-blue-600 tablet:w-auto tablet:pl-0 tablet:text-[18px] tablet:text-blue-700",
												"tablet:flex-1",
											)}>
											{t("txt_video_projects")}
										</span>
									</div>

									<div className="flex w-full items-center tablet:w-auto">
										{/* Free Plan */}
										{/* <span
                      className={clsx(
                        "text-center text-[14px] font-500 text-blue-900 tablet:text-[17px] tablet:text-blue-800",
                        "flex w-full items-center justify-center text-center",
                        "whitespace-pre-line tablet:w-[170px] tablet:px-3 desktop:w-[209px]",
                      )}>
                      {t("fs_tokens_to_videos", {
                        tokens: 10,
                        videos: Math.ceil(
                          10 / TOKENS_TO_VIDEOS_DIVISOR,
                        ),
                      })}
                    </span> */}

										<span
											className={clsx(
												"text-center text-[14px] font-500 text-blue-900 tablet:text-[17px] tablet:text-blue-800",
												"flex w-full items-center justify-center text-center tablet:px-2",
												"whitespace-pre-line tablet:w-[170px] desktop:w-[209px]",
											)}>
											{t("fs_tokens_to_videos", {
												tokens: isYearly
													? plans.advanced.variants?.[value]
															.yearly_tokens
													: plans.advanced.variants?.[value]
															.monthly_tokens,
												videos: Math.ceil(
													((isYearly
														? plans.advanced.variants?.[
																value
														  ].yearly_tokens
														: plans.advanced.variants?.[
																value
														  ].monthly_tokens) ?? 0) /
														TOKENS_TO_VIDEOS_DIVISOR,
												),
											})}
										</span>

										<span
											className={clsx(
												"text-center text-[14px] font-500 text-blue-900 tablet:text-[17px] tablet:text-blue-800",
												"flex w-full items-center justify-center text-center tablet:px-3",
												"tablet:w-[170px] desktop:w-[209px]",
											)}>
											{t("txt_custom")}
										</span>
									</div>
								</div>
							)}
							{section.features.map((feature, index) => {
								return (
									<div
										className={clsx(
											index !== 0 &&
												"border-t border-color-separator py-1 tablet:border-none",
											"flex w-full flex-col rounded-b-[12px] bg-color-cell",
											"tablet:flex-row tablet:py-3 desktop:py-4",
										)}
										key={index}>
										<div className="flex w-[100px] tablet:w-auto tablet:flex-1">
											<span
												className={clsx(
													"pl-3 text-start text-[14px] font-400 text-blue-600 tablet:w-auto tablet:pl-0 tablet:text-[18px] tablet:text-blue-700",
													"tablet:flex-1",
												)}>
												{feature.title}
											</span>
										</div>

										<div className="flex w-full items-center tablet:w-auto">
											{/* Free Plan */}
											{/* <span
                        className={clsx(
                          typeof feature.compareInfo
                            .free === "string" &&
                            "text-center text-[14px] font-500 text-blue-900 tablet:text-[17px] tablet:text-blue-800",
                          "flex w-full items-center justify-center text-center",
                          "tablet:w-[170px] tablet:px-3 desktop:w-[209px]",
                        )}>
                        {compareTransform(
                          feature.compareInfo.free,
                        )}
                      </span> */}

											<span
												className={clsx(
													typeof feature.package_info
														.advanced === "string" &&
														"text-center text-[14px] font-500 text-blue-900 tablet:text-[17px] tablet:text-blue-800",
													"flex w-full items-center justify-center text-center tablet:px-3",
													"tablet:w-[170px] desktop:w-[209px]",
												)}>
												{compareTransform(
													feature.package_info.advanced,
												)}
											</span>

											<span
												className={clsx(
													typeof feature.package_info
														.enterprise === "string" &&
														"text-center text-[14px] font-500 text-blue-900 tablet:text-[17px] tablet:text-blue-800",
													"flex w-full items-center justify-center text-center tablet:px-3",
													"tablet:w-[170px] desktop:w-[209px]",
												)}>
												{compareTransform(
													feature.package_info.enterprise,
												)}
											</span>
										</div>
									</div>
								)
							})}
						</div>
					)
				})}
			</div>
		</div>
	)
}

type CompareArgumentType = "x" | "v" | "@" | string

const compareTransform = (
	arg: CompareArgumentType,
): ReactNode | string => {
	switch (arg) {
		case "x":
			return (
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="19"
					height="18"
					viewBox="0 0 19 18"
					fill="none">
					<path
						d="M10.891 9.133C10.8557 9.09783 10.8359 9.05006 10.8359 9.00025C10.8359 8.95044 10.8557 8.90268 10.891 8.8675L17.8383 1.921C18.2775 1.48111 18.277 0.768399 17.8371 0.329126C17.3972 -0.110148 16.6845 -0.109644 16.2453 0.330251L9.29876 7.27375C9.26359 7.30902 9.21583 7.32884 9.16601 7.32884C9.1162 7.32884 9.06844 7.30902 9.03326 7.27375L2.08676 0.330251C1.64749 -0.10923 0.93512 -0.109398 0.495639 0.329876C0.0561589 0.769149 0.055991 1.48152 0.495264 1.921L7.44101 8.8675C7.47628 8.90268 7.4961 8.95044 7.4961 9.00025C7.4961 9.05006 7.47628 9.09783 7.44101 9.133L0.495264 16.0803C0.211104 16.3645 0.100199 16.7788 0.204325 17.1671C0.30845 17.5553 0.611789 17.8585 1.00007 17.9625C1.38836 18.0664 1.8026 17.9553 2.08676 17.671L9.03326 10.7238C9.06844 10.6885 9.1162 10.6687 9.16601 10.6687C9.21583 10.6687 9.26359 10.6885 9.29876 10.7238L16.2453 17.671C16.6845 18.1105 17.3969 18.1106 17.8364 17.6714C18.2759 17.2321 18.276 16.5197 17.8368 16.0803L10.891 9.133Z"
						fill="#FD2E2E"
					/>
				</svg>
			)
		case "v":
			return (
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="25"
					height="24"
					viewBox="0 0 25 24"
					fill="none">
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M21.2927 5.3128C21.6723 5.71402 21.6547 6.34694 21.2535 6.72647L8.56774 18.7265C8.36889 18.9146 8.10163 19.0129 7.82829 18.9986C7.55495 18.9843 7.29941 18.8586 7.12128 18.6508L3.007 13.8508C2.64757 13.4315 2.69614 12.8002 3.11546 12.4407C3.53479 12.0813 4.16609 12.1299 4.52551 12.5492L7.95631 16.5518L19.8791 5.27353C20.2803 4.894 20.9132 4.91159 21.2927 5.3128Z"
						fill="#09C185"
					/>
				</svg>
			)
		case "@":
			return (
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none">
					<path
						d="M12 12C10 9.33 8 8 6 8C4.93913 8 3.92172 8.42143 3.17157 9.17157C2.42143 9.92172 2 10.9391 2 12C2 13.0609 2.42143 14.0783 3.17157 14.8284C3.92172 15.5786 4.93913 16 6 16C8 16 10 14.67 12 12ZM12 12C14 14.67 16 16 18 16C19.0609 16 20.0783 15.5786 20.8284 14.8284C21.5786 14.0783 22 13.0609 22 12C22 10.9391 21.5786 9.92172 20.8284 9.17157C20.0783 8.42143 19.0609 8 18 8C16 8 14 9.33 12 12Z"
						stroke="#34353D"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			)
		default:
			return arg // Return the string as is for any other case
	}
}
